import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import bannerVideo from "../assets/images/3644403353-preview.mp4";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowRightLong,
  faAward,
  faBath,
  faBed,
  faEnvelope,
  faLocationDot,
  faMagnifyingGlass,
  faPhone,
  faRulerCombined,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import avatar1 from "../assets/images/avatar1.png";
import avatar2 from "../assets/images/avatar3.png";
import placeholder from "../assets/images/house-placeholder.webp";
import { Link } from "react-router-dom";
import imgs from "./temp.json";
import ReactImageGallery from "react-image-gallery";
import { motion } from "framer-motion";
import calc from "../assets/images/shutterstock_2489188913-min.jpg";
import report from "../assets/images/shutterstock_699442570-min.jpg";
import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import formattingFunctions from "../functions/formatting";
import SEO, { pageSEO } from "../components/seo.component";
import isReactSnap from "../utils/isReactSnap";
import globalState from "../utils/globalState";
import { useUserParams } from "../hooks/useUserParams";
import { buttonAnimation, fadeUp, stagger } from "../functions/animations";
import Contact from "./contact.component";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";

function toDisplayText(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value.map(toDisplayText).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    if (value.highlight != null) return toDisplayText(value.highlight);
    if (typeof value.text === "string") return value.text;
    if (typeof value.value === "string") return value.value;
    if (Array.isArray(value.content)) {
      return value.content.map(toDisplayText).filter(Boolean).join(" ");
    }
    for (const nested of Object.values(value)) {
      const text = toDisplayText(nested);
      if (text) return text;
    }
  }
  return "";
}

function toImageSrc(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.url || value.src || value.image || null;
  }
  return null;
}

function normalizeTestimonialsList(rawTestimonials) {
  let list;
  if (Array.isArray(rawTestimonials)) {
    list = rawTestimonials;
  } else if (typeof rawTestimonials === "string") {
    try {
      const parsed = JSON.parse(rawTestimonials);
      return normalizeTestimonialsList(parsed);
    } catch {
      return [];
    }
  } else if (rawTestimonials && typeof rawTestimonials === "object") {
    if (Array.isArray(rawTestimonials.items)) {
      list = rawTestimonials.items;
    } else if (Array.isArray(rawTestimonials.data)) {
      list = rawTestimonials.data;
    } else {
      list = Object.values(rawTestimonials).filter(
        (item) => item != null && typeof item !== "string"
      );
    }
  } else {
    return [];
  }

  return list
    .map((item, index) => {
      if (item == null) return null;
      if (typeof item === "string") {
        return {
          name: `Testimonial ${index + 1}`,
          quote: item,
          image: null,
          location: "",
        };
      }
      if (typeof item !== "object") return null;

      const quote = toDisplayText(
        item.quote ?? item.description ?? item.highlight ?? item.text
      );
      const name =
        toDisplayText(item.name) || `Testimonial ${index + 1}`;
      const location = toDisplayText(
        item.location ?? item.role ?? item.title
      );

      return {
        name,
        quote,
        image: toImageSrc(item.image ?? item.img),
        location,
      };
    })
    .filter((item) => item && (item.quote || item.name));
}

export default function Homepage() {
  const proxyUrl = "https://cors-anywhere.herokuapp.com/";
  const loaded = useRef(false);
  const {
    getCurrentAgentName,
    getCurrentAbout,
    getCurrentHeadshot,
    getCurrentTestimonials,
  } = useUserParams();

  const rawTestimonials = getCurrentTestimonials();
  const testimonials = useMemo(
    () => normalizeTestimonialsList(rawTestimonials),
    [rawTestimonials]
  );

  // State
  // const [listings, setListings] = useState([]); //for production
  const [listings, setListings] = useState(data.value); //for local testing
  const [media, setMedia] = useState(imgs.thumbs.value);
  const [formatted, setFormatted] = useState({});
  const [offset, setOffset] = useState(0);
  const [keyword, setKeyword] = useState("");

  function getData() {
    // Skip API calls during static generation
    if (isReactSnap()) {
      return Promise.resolve({ value: [] });
    }

    var body = {
      url: `https://query.ampre.ca/odata/Property?$filter=(ListOfficeKey eq '${constants.OFFICE_ID}' and StandardStatus eq 'Active')&$orderby=OriginalEntryTimestamp desc,ListingKey&$top=12`,
      needAuth: true,
      bearerToken: constants.TOKEN,
    };

    return new Promise(async (resolve, reject) => {
      try {
        let request = `odata/Property(Q)$filter=(ListOfficeKey(S)eq(S)\'${constants.OFFICE_ID}\'(S)and(S)StandardStatus(S)eq(S)\'Active\')(A)$orderby=OriginalEntryTimestamp(S)desc,ListingKey(A)$top=12`;


        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${request}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function getMedia(listings) {
    // Skip API calls during static generation
    if (isReactSnap() || !listings || listings.length === 0) {
      return Promise.resolve({ value: [] });
    }

    // this is for development
    //var temp = `https://query.ampre.ca/odata/Media?$filter=(`

    // this is for production
    // var temp = `odata/Media?$filter=(`

    if (constants.ENVIRONMENT === "local") {
      var temp = `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "', ";
      }
      temp = temp.slice(0, -2);
      temp =
        temp +
        ") and ImageSizeDescription eq 'Medium' and PreferredPhotoYN eq true";
    } else {
      var temp = `/odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "',";
      }
      temp = temp.slice(0, -1);
      temp =
        temp +
        ")(S)and(S)ImageSizeDescription(S)eq(S)'Medium'(S)and(S)PreferredPhotoYN(S)eq(S)true";
    }
    var body = {
      url: temp,
      needAuth: true,
      bearerToken: constants.TOKEN,
      // Accept: 'application/json',
      // Authorization: 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ2ZW5kb3IvdHJyZWIvNjUyMCIsImF1ZCI6IkFtcFVzZXJzUHJkIiwicm9sZXMiOlsiQW1wVmVuZG9yIl0sImlzcyI6InByb2QuYW1wcmUuY2EiLCJleHAiOjI1MzQwMjMwMDc5OSwiaWF0IjoxNzMwMjI1OTA1LCJzdWJqZWN0VHlwZSI6InZlbmRvciIsInN1YmplY3RLZXkiOiI2NTIwIiwianRpIjoiMGYyMjk3OTY1Y2NjN2U4MSIsImN1c3RvbWVyTmFtZSI6InRycmViIn0.BMvVrtwah8swIzW38UMs6yKF-HM4QO0bZu81blYx6J4'
    };

    return new Promise(async (resolve, reject) => {
      try {
        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${temp}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function formatMedia(media) {
    var temp = {};

    for (let img of media) {
      if (temp[img.ResourceRecordKey]) {
        temp[img.ResourceRecordKey] = [...temp[img.ResourceRecordKey], img];
      } else {
        temp[img.ResourceRecordKey] = [img];
      }
    }
    return temp;
  }

  const getURLParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      userId: urlParams.get("userId"),
    };
  };

  // Function to fetch template settings from MongoDB
  const fetchTemplateSettings = async (userId) => {
    try {
      const apiKey = process.env.REACT_APP_API_KEY;
      const response = await fetch(
        `https://agent.proagentsolutions.com/MongoDB/GetTemplateSettingsByUserId?userId=${userId}&apiKey=${apiKey}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const templateSettings = await response.json();
      console.log("Template settings fetched:", templateSettings);

      if (templateSettings) {
        const settingsData = templateSettings.data || templateSettings;

        const mappedUserData = {
          userId: userId,
          agentName: settingsData.agentName || settingsData.AGENT_NAME,
          agentTitle: settingsData.agentTitle || settingsData.AGENT_TITLE,
          agentNumber: settingsData.agentNumber || settingsData.AGENT_NUMBER,
          agentEmail: settingsData.agentEmail || settingsData.AGENT_EMAIL,
          brokerage: settingsData.brokerage || settingsData.BROKERAGE,
          ...settingsData,
        };

        globalState.setUserData(mappedUserData);

        console.log("Template settings data:", settingsData);
        console.log("Template settings mapped to user data:", mappedUserData);
      }

      return templateSettings;
    } catch (error) {
      console.error("Error fetching template settings:", error);
      return null;
    }
  };

  // Load template settings when userId is present in URL
  useEffect(() => {
    const urlParams = getURLParameters();
    if (urlParams.userId) {
      globalState.setUserId(urlParams.userId);
      fetchTemplateSettings(urlParams.userId).then(() => {
        console.log(
          "Global State Updated with Template Settings:",
          globalState.getDebugInfo()
        );
      });
      console.log("Global State Updated:", globalState.getDebugInfo());
    }
  }, []);

  // Data fetching effect with React Snap compatibility
  useEffect(() => {
    if (isReactSnap()) return; // Skip fetching during react-snap
    const isReactSnapEnv =
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes("ReactSnap");
    const isBrowser = typeof window !== "undefined";

    if (isBrowser && !isReactSnapEnv && !loaded.current) {
      getData()
        .then((data) => {
          setListings(data.value);
          getMedia(data.value)
            .then((d) => {
              setMedia(d.value);
              setFormatted(formatMedia(d.value));
            })
            .catch((err) => {
              console.log(err);
            });
        })
        .catch((err) => {
          console.log(err);
        });
      loaded.current = true;
    }
  }, []);

  function slugify(str) {
    str = str.replace(/^\s+|\s+$/g, "");
    str = str.toLowerCase();
    str = str
      .replace(/[^a-z0-9 -]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
    return str;
  }

  const seoConfig = pageSEO.home();

  return (
    <>
      <SEO {...seoConfig} />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="mx-auto w-full">
          <div className="relative h-screen">

            {/* Image */}
            <Swiper
              modules={[EffectFade, Autoplay]}
              effect="fade"
              fadeEffect={{
                crossFade: true,
              }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              speed={1500}
              loop
              className="absolute inset-0 h-full w-full z-0"
            >
              {constants.HEROIMAGES.map((image, index) => (
                <SwiperSlide key={index}>
                  <img
                    src={image}
                    alt={`Hero ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            <div class="absolute inset-0 bg-black/40 z-10"></div>

            {/* Floating Content Card */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="absolute m-5 bottom-0 left-0 md:m-0 md:bottom-16 md:left-40 md:translate-x-0 md:translate-y-0 max-w-xl py-8 px-6 md:p-10 shadow-xl z-20"
            >
              <div className="flex items-center gap-4">
                <span className="uppercase tracking-[0.4em] text-lg text-cottage-highlight font-semibold">
                  Trusted Real Estate
                </span>
              </div>
              <h1 className="mt-8 font-serif text-5xl lg:text-7xl leading-[0.95] text-white">
                Helping You
                <br />
                Find Home.
              </h1>
              <p className="mt-8 text-lg leading-8 text-cottage-secondary">
                From first homes to luxury estates, discover a
                personalized real estate experience built on local
                expertise, trusted guidance, and exceptional results.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/listings"
                  className="btn-cottage"
                >
                  Explore Listings
                </Link>

                <Link
                  to="/contact"
                  className="btn-cottage-outline"
                >
                  Contact
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section >

      {/* About Section */}
      <section className="py-24 overflow-hidden">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -80 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7"
            >
              <div className="mb-8">
                <span className="uppercase tracking-[0.4em] font-semibold text-cottage-primary text-lg">
                  About
                </span>
              </div>

              <h2 className="font-serif text-5xl lg:text-6xl leading-[0.95]">
                {getCurrentAgentName()}
              </h2>

              <p className="mt-8 text-lg leading-8 text-cottage-muted whitespace-pre-line">
                {getCurrentAbout()}
              </p>

              <Link
                to="/about"
                className="inline-flex mt-10 btn-cottage"
              >
                Discover More
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 80 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative">
                <img
                  src={getCurrentHeadshot()}
                  alt={getCurrentAgentName()}
                  className="h-[300px] w-full rounded-lg object-cover"
                />

                <div className="absolute bottom-0 right-0 m-4 md:bottom-5 md:right-5 md:m-0 rounded-lg bg-white/90 backdrop-blur-lg px-5 py-6 shadow-xl">
                  <p className="font-serif text-3xl text-cottage-primary">
                    Trusted Guidance
                  </p>
                  <p className="mt-2 text-sm uppercase tracking-[0.2em] text-cottage-primary">
                    Every Step Of The Way
                  </p>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-6">
                <div className="border rounded-lg border-cottage-primary flex flex-col gap-5 items-center justify-center h-40">
                  <p className="font-serif text-4xl text-cottage-accent">15+</p>
                  <span className="text-sm uppercase tracking-widest text-cottage-muted font-medium text-center">
                    Years <br></br> Experience
                  </span>
                </div>

                <div className="border rounded-lg border-cottage-primary flex flex-col gap-5 items-center justify-center h-40">
                  <p className="font-serif text-4xl text-cottage-accent">250+</p>
                  <span className="text-sm uppercase tracking-widest text-cottage-muted font-medium text-center">
                    Homes Sold
                  </span>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Featured Listings Section */}
      <motion.section
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="bg-cottage-secondary"
      >
        <div className="px-6 py-24 mx-auto max-w-[1600px]">
          <div className="mb-14 text-center">
            <motion.div variants={fadeUp} className="mb-5">
              <p className="uppercase tracking-[0.4em] font-medium text-cottage-primary">
                Curated Properties
              </p>
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="font-serif text-5xl lg:text-6xl leading-none"
            >
              Featured Listings
            </motion.h2>
          </div>

          <motion.div
            variants={fadeUp}
          >
            <Swiper
              modules={[Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              loop
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              breakpoints={{
                0: {
                  slidesPerView: 1,
                },
                786: {
                  slidesPerView: 2,
                },
                1280: {
                  slidesPerView: 3,
                },
              }}
            >
              {listings?.slice(0, 6).map((prop) => (
                <SwiperSlide key={prop.ListingKey}>
                  <Link
                    to={`/listing/${prop.ListingKey}`}
                    className="group block"
                  >
                    <div className="overflow-hidden rounded-lg bg-white/70 shadow-xl transition-all duration-500">

                      <div className="relative h-[320px] overflow-hidden">
                        <img
                          // src={
                          //   formatted[prop.ListingKey]?.find((img) => img.Order === 0)?.MediaURL ??
                          //   formatted[prop.ListingKey]?.[0]?.MediaURL ??
                          //   placeholder
                          // }
                          src={prop.MainImgUrl} //dummy image
                          alt={`${prop.StreetNumber} ${prop.StreetName}`}
                          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                        />

                        {/* Price Badge */}
                        <div className="w-full absolute top-0 flex flex-col lg:flex-row justify-between lg:items-center gap-3 p-3">
                          <div className="rounded-full bg-cottage-accent px-5 py-2 text-lg font-semibold text-white shadow-lg w-fit">
                            ${prop.ListPrice?.toLocaleString()}
                          </div>

                          {/* Property Type */}
                          <div className="rounded-full bg-black/40 px-4 py-2 text-sm uppercase tracking-wider text-white backdrop-blur-md w-fit">
                            {prop.PropertyType}
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        {/* Location */}
                        <p className="mb-3 flex items-center text-sm uppercase tracking-[0.3em] text-cottage-primary">
                          <FontAwesomeIcon
                            icon={faLocationDot}
                            className="mr-2"
                          />
                          {prop.CityRegion ?? prop.CrossStreet}
                        </p>

                        {/* Address */}
                        <h3 className="text-xl font-light leading-tight">
                          {prop.StreetNumber} {prop.StreetName} {prop.StreetSuffix}
                        </h3>

                        {/* Divider */}
                        <div className="my-5 h-px bg-gray-300" />

                        {/* Features */}
                        <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
                          <div className="flex gap-6 text-cottage-primary">
                            {prop.PropertyType !== "Commercial" && (
                              <>
                                <div className="flex items-center gap-2">
                                  <FontAwesomeIcon
                                    icon={faBed}
                                    className="text-cottage-accent"
                                  />
                                  <span>
                                    {prop.BedroomsTotal || prop.BedroomsTotalInteger}
                                  </span>
                                </div>

                                <div className="flex items-center gap-2">
                                  <FontAwesomeIcon
                                    icon={faBath}
                                    className="text-cottage-accent"
                                  />
                                  <span>
                                    {prop.BathroomsTotalInteger}
                                  </span>
                                </div>
                              </>
                            )}

                            <div className="flex items-center gap-2">
                              <FontAwesomeIcon
                                icon={faRulerCombined}
                                className="text-cottage-accent"
                              />
                              <span>
                                {prop.LivingAreaRange ?? "--"} sq.ft.
                              </span>
                            </div>
                          </div>

                          {/* CTA */}
                          <div className=" py-2 px-4 bg-cottage-accent border rounded-lg border-transparent text-white w-fit group-hover:bg-cottage-primary">
                            View
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>

          <motion.div variants={buttonAnimation} className="mt-14 flex justify-center">
            <Link to="/listings" className="btn-cottage">
              View All Listings <FontAwesomeIcon icon={faArrowRight} className="ml-3" />
            </Link>
          </motion.div>
        </div>
      </motion.section >

      {/* Testimonials Section */}
      <section>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mx-auto max-w-[1600px] px-6 py-24"
        >
          <div className="mb-14 text-center">
            <motion.div variants={fadeUp} className="mb-5">
              <p className="uppercase tracking-[0.4em] font-medium text-cottage-primary">
                client stories
              </p>
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="font-serif text-5xl lg:text-6xl leading-none"
            >
              Trusted By Our Clients
            </motion.h2>
          </div>

          <motion.div variants={fadeUp} className="md:px-12 xl:px-0">
            <Swiper
              modules={[Autoplay]}
              loop
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              spaceBetween={30}
              slidesPerView={1}
              breakpoints={{
                0: {
                  slidesPerView: 1,
                },
                786: {
                  slidesPerView: 2,
                },
                1280: {
                  slidesPerView: 3,
                },
              }}
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <motion.div
                    variants={fadeUp}
                    className="group relative flex h-[220px] flex-col overflow-hidden rounded-xl bg-cottage-secondary p-8 shadow-lg"
                  >
                    {/* Sliding green background */}
                    <div
                      className="absolute inset-x-0 bottom-0 h-full translate-y-full bg-cottage-accent transition-transform duration-500 ease-out group-hover:translate-y-0"
                    />

                    {/* Content */}
                    <div className="relative z-10 flex h-full flex-col">
                      <p className="line-clamp-3 overflow-hidden leading-6 text-cottage-muted tracking-wider transition-colors duration-500 group-hover:text-white">
                        "{testimonial.quote}"
                      </p>

                      <div className="mt-auto flex items-center gap-4">

                        {testimonial.image ? (
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="h-12 w-12 rounded-full object-cover"
                          />
                        ) : (
                          <div
                            className="flex h-12 w-12 items-center justify-center rounded-full bg-cottage-accent font-semibold text-white transition-colors duration-500 group-hover:bg-white group-hover:text-cottage-accent"
                          >
                            {testimonial.name
                              .split(" ")
                              .map((word) => word[0])
                              .slice(0, 2)
                              .join("")
                              .toUpperCase()}
                          </div>
                        )}

                        <div>
                          <h3 className="font-semibold tracking-wider text-summit-muted transition-colors duration-500 group-hover:text-white">
                            {testimonial.name}
                          </h3>

                          <p className="text-sm uppercase tracking-widest text-summit-highlight transition-colors duration-500 group-hover:text-white">
                            {testimonial.location}
                          </p>
                        </div>
                      </div>

                    </div>
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </motion.div>
      </section>

      {/* Useful Tools Section */}
      <section className="border-b border-cottage-secondary">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mx-auto max-w-[1600px] px-6 py-24"
        >
          <div className="mb-14">
            <motion.div variants={fadeUp} className="mb-5">
              <p className="uppercase tracking-[0.4em] font-medium text-cottage-primary">
                Real Estate Resources
              </p>
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="font-serif text-5xl lg:text-6xl leading-none"
            >
              Useful Tools
            </motion.h2>
          </div>

          <motion.div variants={fadeUp} className="grid gap-8 md:grid-cols-3">
            {[
              {
                title: "Mortgage Calculator",
                description: "Plan your purchase with a clear understanding of your budget and monthly costs.",
                image: calc,
                link: "/calculator",
              },
              {
                title: "Market Reports",
                description: "Access valuable insights about pricing trends and your local real estate market.",
                image: report,
                link: "/real-estate-reports",
              },
              {
                title: "Home Evaluation",
                description: "Discover your home's current value with expert market analysis.",
                image: house,
                link: "/evaluation",
              },
            ].map((card, index) => (
              <Link
                key={index}
                to={card.link}
                className="group"
              >
                <div className="relative overflow-hidden rounded-lg bg-white border border-harbour-border transition-all duration-500 hover:-translate-y-3 hover:shadow-xl">

                  <div className="relative h-[250px] overflow-hidden">
                    <img
                      src={card.image}
                      alt={card.title}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>

                  <div className="relative p-8 bg-cottage-secondary/70">
                    <div className="absolute -top-7 right-8 flex h-14 w-14 items-center justify-center rounded-full bg-cottage-accent text-white shadow-xl transition duration-500">
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </div>

                    <p className="text-xs uppercase tracking-[0.35em] text-cottage-primary">
                      Discover
                    </p>

                    <h3 className="mt-4 font-serif text-3xl text-cottage-primary">
                      {card.title}
                    </h3>

                    <p className="mt-5 text-sm h-14 leading-5 text-cottage-muted line-clamp-3">
                      {card.description}
                    </p>

                    <div className="mt-8 h-px w-full bg-cottage-accent/50" />

                    <span className="mt-6 inline-block text-xs uppercase tracking-[0.3em] text-cottage-primary transition group-hover:text-cottage-accent">
                      Explore Tool
                    </span>

                  </div>

                </div>
              </Link>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Contact />
    </>
  );
}
